import React, { useState } from 'react'
import { registerComponent } from 'react-register-dom'
import { cls } from '@utils/classnames'
import { normalizeDatePasteValue } from '@utils/normalizeDatePasteValue'

type FormDataTableEntry = Record<string, string>
type FormDataTableValues = Record<string, FormDataTableEntry>
type FormDataTableJson = Record<string, FormDataTableValues>

type FormDataParsedJsonData = {
  id: string
  label: string
  name: string
  columnsProperties: FormDataTableColumnProperty[]
  maxNumberOfRows: number
  defaultNumberOfRows?: number
  addMoreButtonLabel?: string
  copyExcelDataInfoText?: string
  requiredFieldsErrorMessage?: string
}

type FormDataTableColumnProperty = {
  columnWidth?: number
  headerLabel: string
  placeholder?: string
  required?: boolean
  type: 'text' | 'number' | 'date'
  validationPattern?: string
}

const TABLE_ROW_SELECTOR = '.cmp-form-data-table__table-row'
const TABLE_INPUT_SELECTOR = '.cmp-form-data-table__table-input'
const DEFAULT_REQUIRED_FIELDS_ERROR_MESSAGE = 'Required fields are missing. Please check and complete the table.'

const fillRowValues = (row: HTMLTableRowElement, cells: string[]) => {
  const inputs = row.querySelectorAll<HTMLInputElement>(TABLE_INPUT_SELECTOR)

  for (const [colIndex, value] of cells.entries()) {
    const input = inputs[colIndex]
    if (input) {
      const trimmedValue = value.trim()
      input.value = input.type === 'date' ? normalizeDatePasteValue(trimmedValue) : trimmedValue
    }
  }
}

const FormDataTable: React.FC<{
  jsonData: string
}> = ({ jsonData }) => {
  let parsedJsonData: FormDataParsedJsonData

  try {
    parsedJsonData = JSON.parse(jsonData)
  } catch (error) {
    console.error('Invalid JSON data provided to FormDataTable component:', error)
    return <div className="cmp-form-data-table__error">Invalid configuration for the form data table.</div>
  }

  const {
    label,
    name,
    columnsProperties,
    maxNumberOfRows,
    defaultNumberOfRows,
    addMoreButtonLabel = 'Add more',
    copyExcelDataInfoText = '',
    requiredFieldsErrorMessage = DEFAULT_REQUIRED_FIELDS_ERROR_MESSAGE,
  } = parsedJsonData as FormDataParsedJsonData

  const hasRequiredColumn = columnsProperties.some((column) => column.required)

  const formDataTableBody = React.useRef<HTMLTableSectionElement>(null)
  const rowReferences = React.useRef<Record<string, HTMLTableRowElement | null>>({})
  const formDataTableFromSessionStorage = sessionStorage.getItem('formDataTables')

  const parsedFormDataTableFromSessionStorage: FormDataTableJson | null = formDataTableFromSessionStorage
    ? JSON.parse(formDataTableFromSessionStorage)
    : null

  // only numeric keys should be considered as rows, as there are also keys for the table label, type and name
  const numericKeysFormDataTable: FormDataTableValues = parsedFormDataTableFromSessionStorage?.[name]
    ? (Object.fromEntries(
        Object.entries(parsedFormDataTableFromSessionStorage[name]).filter(([key]) => /^\d+$/.test(key)),
      ) as FormDataTableValues)
    : {}

  const defaultNumberOfRowsValue = Math.min(defaultNumberOfRows ?? 3, maxNumberOfRows)
  const defaultFormDataTableValues: FormDataTableValues = Object.fromEntries(
    Array.from({ length: defaultNumberOfRowsValue }, (_, index) => [index, {}]),
  )

  const formDataTableValues: FormDataTableValues =
    Object.keys(numericKeysFormDataTable).length > 0 ? numericKeysFormDataTable : defaultFormDataTableValues

  const [currentNumberOfRows, setCurrentNumberOfRows] = useState(Object.keys(formDataTableValues).length)
  const [showRequiredFieldsError, setShowRequiredFieldsError] = useState(false)

  // Row indices must never be reused, even after a row is deleted, otherwise two rows could end up
  // sharing the same input `name` and get merged into one another on submit.
  const initialRowKeys = Object.keys(formDataTableValues).map(Number)
  const nextRowIndex = React.useRef(initialRowKeys.length > 0 ? Math.max(...initialRowKeys) + 1 : 1)

  // Shows one message for the whole table rather than per-field errors. `invalid` fires when the
  // form's submit-time checkValidity()/reportValidity() call finds a required field still empty —
  // once per invalid field, so this also marks each one touched to show its own red border.
  const handleInputInvalid = (event: { preventDefault: () => void; currentTarget: EventTarget | null }) => {
    event.preventDefault()

    const input = event.currentTarget as HTMLInputElement
    if (!input.classList.contains('touched')) {
      input.classList.add('touched')
    }

    setShowRequiredFieldsError(true)
  }

  // Once shown, hide the message again as soon as every required field in the table is valid.
  const revalidateTable = () => {
    if (formDataTableBody.current && !formDataTableBody.current.querySelector(`${TABLE_INPUT_SELECTOR}:invalid`)) {
      setShowRequiredFieldsError(false)
    }
  }

  const removeRow = (row: HTMLTableRowElement) => {
    if (!formDataTableBody.current) {
      return
    }

    const rows = formDataTableBody.current.querySelectorAll(TABLE_ROW_SELECTOR)
    if (rows.length <= 1) {
      return
    }

    row.remove()
    setCurrentNumberOfRows((previous) => previous - 1)
  }

  const createActionsCell = (row: HTMLTableRowElement) => {
    const actionsCell = document.createElement('td')
    actionsCell.className = 'cmp-form-data-table__table-cell cmp-form-data-table__table-cell--actions'
    const removeButton = document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = 'cmp-form-data-table__remove-row-button'
    removeButton.setAttribute('aria-label', 'Remove row')
    removeButton.innerHTML = '<i class="ri-close-line" aria-hidden="true"></i>'
    removeButton.addEventListener('click', () => removeRow(row))
    actionsCell.append(removeButton)
    return actionsCell
  }

  // At least one row must always remain, so the remove button/cell is only shown once there's more
  // than one row. Row numbers reflect display position, not the (non-contiguous) row index used in
  // input names, so both must be recalculated whenever rows are added or removed.
  React.useEffect(() => {
    const rows = [...(formDataTableBody.current?.querySelectorAll<HTMLTableRowElement>(TABLE_ROW_SELECTOR) ?? [])]
    const shouldHaveActionsCell = rows.length > 1

    for (const [index, row] of rows.entries()) {
      const actionsCell = row.querySelector<HTMLElement>('.cmp-form-data-table__table-cell--actions')
      if (shouldHaveActionsCell && !actionsCell) {
        row.append(createActionsCell(row))
      } else if (!shouldHaveActionsCell && actionsCell) {
        actionsCell.remove()
      }

      const rowNumber = row.querySelector<HTMLElement>('.cmp-form-data-table__row-number')
      if (rowNumber) {
        rowNumber.textContent = String(index + 1)
      }
    }
  }, [currentNumberOfRows])

  const buildRow = (rowIndex: number) => {
    const newRow = document.createElement('tr')
    newRow.className = 'cmp-form-data-table__table-row'

    const rowNumberCell = document.createElement('td')
    rowNumberCell.className = 'cmp-form-data-table__table-cell cmp-form-data-table__table-cell--row-number'
    const rowNumber = document.createElement('span')
    rowNumber.className = 'cmp-form-data-table__row-number'
    rowNumberCell.append(rowNumber)
    newRow.append(rowNumberCell)

    for (const [_, column] of columnsProperties.entries()) {
      const newCell = document.createElement('td')
      newCell.className = 'cmp-form-data-table__table-cell'
      const input = document.createElement('input')
      input.type = column.type
      input.name = `${name}_${rowIndex}_${column.headerLabel}`
      input.className = 'cmp-form-data-table__table-input'
      input.maxLength = 255
      input.placeholder = column.placeholder || ''

      if (column.required) {
        input.required = true
      }

      if (column.validationPattern) {
        input.pattern = column.validationPattern
      }

      input.addEventListener('blur', (event) => {
        const target = event.target as HTMLInputElement
        if (!target.classList.contains('touched')) {
          target.classList.add('touched')
        }
        revalidateTable()
      })

      input.addEventListener('invalid', handleInputInvalid)

      newCell.append(input)
      newRow.append(newCell)
    }

    // buildRow only runs when the table is about to have more than one row, so the new row always gets it.
    newRow.append(createActionsCell(newRow))

    return newRow
  }

  const addRow = () => {
    if (formDataTableBody.current && currentNumberOfRows < maxNumberOfRows) {
      const newRow = buildRow(nextRowIndex.current)
      nextRowIndex.current += 1
      setCurrentNumberOfRows((previous) => previous + 1)
      formDataTableBody.current.append(newRow)
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLTableElement>) => {
    const clipboardData = event.clipboardData?.getData('text/plain')

    // Only apply "paste table from spreadsheet" behavior when the clipboard content
    // is tab-separated. Otherwise, let the browser handle paste normally.
    if (!clipboardData || !clipboardData.includes('\t') || !formDataTableBody.current) {
      return
    }

    event.preventDefault()

    const pastedRows = clipboardData.split('\n').filter((row) => row.trim())
    const existingRows = [...formDataTableBody.current.querySelectorAll<HTMLTableRowElement>(TABLE_ROW_SELECTOR)]

    // Start filling from the row that was focused when the paste happened, instead of always appending at the end.
    const focusedRow = (event.target as HTMLElement).closest<HTMLTableRowElement>(TABLE_ROW_SELECTOR)
    const focusedRowIndex = focusedRow ? existingRows.indexOf(focusedRow) : -1
    const appendStartIndex = focusedRowIndex === -1 ? existingRows.length : focusedRowIndex

    const neededTotalRows = Math.min(appendStartIndex + pastedRows.length, maxNumberOfRows)
    const rowsToAdd = Math.max(0, neededTotalRows - existingRows.length)
    const newRows = Array.from({ length: rowsToAdd }, (_, index) => buildRow(nextRowIndex.current + index))

    for (const row of newRows) formDataTableBody.current?.append(row)
    if (rowsToAdd > 0) {
      nextRowIndex.current += rowsToAdd
      setCurrentNumberOfRows((previous) => previous + rowsToAdd)
    }

    const targetRows = [...existingRows, ...newRows]

    for (const [rowIndex, row] of pastedRows.entries()) {
      const targetRow = targetRows[appendStartIndex + rowIndex]
      if (targetRow) {
        fillRowValues(targetRow, row.split('\t'))
      }
    }
  }

  return (
    <>
      <div className="cmp-form-data-table__container">
        <table className="cmp-form-data-table__table" onPaste={handlePaste}>
          <caption
            className={cls({
              'cmp-form-data-table__table-caption': true,
              'cmp-form-data-table__table-caption--is-invalid': showRequiredFieldsError,
            })}
          >
            {label}
            {hasRequiredColumn && <span aria-hidden="true">*</span>}
            {copyExcelDataInfoText && (
              <span className="cmp-form-data-table__copy-excel-info">{copyExcelDataInfoText}</span>
            )}
          </caption>
          <thead>
            <tr className="cmp-form-data-table__table-header-row">
              <th className="cmp-form-data-table__table-header cmp-form-data-table__table-header--row-number">#</th>
              {columnsProperties.map((column: FormDataTableColumnProperty, index: number) => (
                <th
                  key={index}
                  className="cmp-form-data-table__table-header"
                  style={column.columnWidth ? { width: `${column.columnWidth}%` } : undefined}
                  colSpan={index === columnsProperties.length - 1 && currentNumberOfRows > 1 ? 2 : undefined}
                >
                  {column.headerLabel}
                </th>
              ))}
            </tr>
          </thead>
          <tbody ref={formDataTableBody}>
            {Object.keys(formDataTableValues)?.map((rowKey, position) => {
              return (
                <tr
                  key={rowKey}
                  className="cmp-form-data-table__table-row"
                  ref={(row) => {
                    rowReferences.current[rowKey] = row
                  }}
                >
                  <td className="cmp-form-data-table__table-cell cmp-form-data-table__table-cell--row-number">
                    <span className="cmp-form-data-table__row-number">{position + 1}</span>
                  </td>
                  {columnsProperties.map((column: FormDataTableColumnProperty, index: number) => (
                    <td key={index} className="cmp-form-data-table__table-cell">
                      <input
                        type={column.type}
                        name={`${name}_${rowKey}_${column.headerLabel}`}
                        className="cmp-form-data-table__table-input"
                        maxLength={255}
                        defaultValue={formDataTableValues?.[rowKey]?.[column.headerLabel] || ''}
                        placeholder={column.placeholder}
                        required={column.required}
                        pattern={column.validationPattern || undefined}
                        onBlur={(event) => {
                          if (!event.target.classList.contains('touched')) {
                            event.target.classList.add('touched')
                          }
                          revalidateTable()
                        }}
                        onInvalid={handleInputInvalid}
                      />
                    </td>
                  ))}
                  {currentNumberOfRows > 1 && (
                    <td className="cmp-form-data-table__table-cell cmp-form-data-table__table-cell--actions">
                      <button
                        type="button"
                        className="cmp-form-data-table__remove-row-button"
                        aria-label="Remove row"
                        onClick={() => {
                          const row = rowReferences.current[rowKey]
                          if (row) {
                            removeRow(row)
                          }
                        }}
                      >
                        <i className="ri-close-line" aria-hidden="true"></i>
                      </button>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {showRequiredFieldsError && (
        <p className="cmp-form-data-table__validation-message" role="alert">
          {requiredFieldsErrorMessage}
        </p>
      )}
      <input type="hidden" name={`${name}_label`} value={label} />
      {maxNumberOfRows > currentNumberOfRows && (
        <button type="button" className="cmp-form-data-table__add-row-button cmp-form-button" onClick={addRow}>
          {addMoreButtonLabel}
        </button>
      )}
    </>
  )
}

registerComponent('FormDataTable', FormDataTable)

export default FormDataTable
